import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { UserAvatar } from "@/components/common/UserAvatar";
import { EmptyState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollText, Search } from "lucide-react";
import { toast } from "sonner";

export default function AuditLog() {
  const { currentAccount } = useAuth();
  const [data, setData] = useState({ entries: [], total: 0, page: 1, page_size: 50, actions: [], record_types: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [action, setAction] = useState("");
  const [recordType, setRecordType] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/audit-log", { params: { q, action, record_type: recordType, page, page_size: 50 } }); setData(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [q, action, recordType, page, currentAccount]);
  useEffect(() => { load(); }, [load]);

  const fmt = (v) => v ? JSON.stringify(v).slice(0, 60) : "—";

  const columns = [
    { key: "timestamp", header: "When", render: (r) => <span className="text-xs text-slate-500 whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</span> },
    { key: "actor", header: "Actor", render: (r) => (
      <div className="flex items-center gap-2"><UserAvatar name={r.actor_name} size={26} /><span className="text-sm text-slate-700">{r.actor_name}</span></div>) },
    { key: "action", header: "Action", render: (r) => <span className="font-mono text-xs bg-slate-100 rounded px-2 py-0.5 text-slate-700">{r.action}</span> },
    { key: "record_type", header: "Record", render: (r) => <span className="text-sm text-slate-600">{r.record_type}</span> },
    { key: "change", header: "Change", render: (r) => (
      <span className="text-xs text-slate-500">{r.previous_value ? `${fmt(r.previous_value)} → ` : ""}{fmt(r.new_value)}</span>) },
  ];

  return (
    <div>
      <PageHeader title="Audit Log" description="Append-only record of administrative and security actions. Cannot be edited or deleted." />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-xs flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search actor or action…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="pl-9" data-testid="audit-search" />
        </div>
        <Select value={action || "all"} onValueChange={(v) => { setAction(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-52" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="all">All actions</SelectItem>
            {data.actions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={recordType || "all"} onValueChange={(v) => { setRecordType(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-48" data-testid="audit-type-filter"><SelectValue placeholder="All records" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72"><SelectItem value="all">All records</SelectItem>
            {data.record_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <DataTable columns={columns} rows={data.entries} loading={loading} testId="audit-table"
        page={data.page} pageSize={data.page_size} total={data.total} onPageChange={setPage}
        empty={<EmptyState icon={ScrollText} title="No audit entries" description="Administrative actions will be recorded here." />} />
    </div>
  );
}
